const DEFAULT_MIN_HIDDEN_MS = 3000
const DEFAULT_THROTTLE_MS = 60000

/**
 * Runs `onReturn` when the tab comes back to the foreground after a genuine hidden stretch — and no
 * more than once per `throttleMs`.
 *
 * Why the guards, and why this is shared: this microapp runs in an iframe inside the appNext Angular
 * shell. Angular change detection re-attaches the iframe on ordinary interactions (mouse movement
 * included), and each re-attach flips the iframe document hidden→visible for a fraction of a second
 * — a real `visibilitychange` event, not a real tab switch. A bare "refresh on visible" handler
 * therefore fired a full network refresh on nearly every mouse move; three separate hooks
 * (message counts, message badges, stage facets) each did this, so one mouse move produced a
 * my-states + summaries + search burst.
 *
 * Two guards fix that:
 *   1. `minHiddenMs` — ignore the sub-second reattach flicker; only a real hidden stretch counts.
 *   2. `throttleMs`  — even for real tab-returns, refresh at most once per window, so rapidly
 *      switching between apps doesn't re-fetch when we refreshed moments ago. A return after a
 *      genuinely long absence still refreshes.
 *
 * Route every "fresh on tab return" through here. Returns an unsubscribe function.
 */
export function onTabRefocus(
  onReturn: () => void,
  { minHiddenMs = DEFAULT_MIN_HIDDEN_MS, throttleMs = DEFAULT_THROTTLE_MS }: { minHiddenMs?: number; throttleMs?: number } = {},
): () => void {
  // Seed from the state at attach time: `visibilitychange` never fires for the state the document was
  // already in, so a tab that mounts while backgrounded (middle-clicked open) would otherwise see its
  // first hidden→visible transition with hiddenAt still null and never refresh on that first reveal.
  let hiddenAt: number | null = document.visibilityState === 'hidden' ? Date.now() : null
  let lastReturnAt = 0
  const handler = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      return
    }
    // Became visible. Only a return from a real hidden stretch counts (not sub-second reattach
    // flicker), and not more often than the throttle window.
    const now = Date.now()
    const hiddenFor = hiddenAt === null ? 0 : now - hiddenAt
    hiddenAt = null
    const throttled = now - lastReturnAt < throttleMs
    const willRefresh = hiddenFor >= minHiddenMs && !throttled
    if (!willRefresh) return
    lastReturnAt = now
    onReturn()
  }
  document.addEventListener('visibilitychange', handler)
  return () => document.removeEventListener('visibilitychange', handler)
}
